/**
 * Display helpers for the options simulator. Every number goes through `fmt`
 * (utils/priceFormat; scripts/check-price-formatters.mjs). The engine is native USD; ₹ only
 * here, at the edge, at the run's USD→INR rate.
 */
import { fmt } from '../../utils/priceFormat';

export const money = (usd: number | null | undefined, fx: number, decimals = 0): string =>
  usd == null || !Number.isFinite(usd) ? '—' : fmt.inr(usd * fx, decimals);

export const signedMoney = (usd: number | null | undefined, fx: number, decimals = 0): string => {
  if (usd == null || !Number.isFinite(usd)) return '—';
  const v = usd * fx;
  return `${v >= 0 ? '+' : '−'}${fmt.inr(Math.abs(v), decimals)}`;
};

export const usd = (v: number | null | undefined, decimals = 0): string =>
  v == null || !Number.isFinite(v) ? '—' : `${v < 0 ? '−' : ''}$${fmt.num(Math.abs(v), decimals)}`;

export const signedUsd = (v: number | null | undefined, decimals = 0): string =>
  v == null || !Number.isFinite(v) ? '—' : `${v >= 0 ? '+' : '−'}$${fmt.num(Math.abs(v), decimals)}`;

export const num = (v: number | null | undefined, decimals = 2): string => fmt.num(v, decimals);

export const pct = (v: number | null | undefined, decimals = 0): string =>
  v == null || !Number.isFinite(v) ? '—' : `${fmt.num(v, decimals)}%`;

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 21 Nov 2025 (UTC). */
export const dateUtc = (t: number): string => {
  const d = new Date(t * 1000);
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/** 21 Nov 12:05 (UTC). */
export const dateTimeUtc = (t: number): string => {
  const d = new Date(t * 1000);
  const hh = String(d.getUTCHours()).padStart(2, '0'), mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${hh}:${mm}`;
};

export const isoDay = (t: number): string => new Date(t * 1000).toISOString().slice(0, 10);

/** C-BTC-96000-211125 → "C 96,000 · 21 Nov"; BTCUSD → "BTCUSD perp". */
export const legLabel = (symbol: string): string => {
  const m = /^([CP])-BTC-(\d+(?:\.\d+)?)-(\d{2})(\d{2})(\d{2})$/.exec(symbol);
  if (!m) return symbol === 'BTCUSD' ? 'BTCUSD perp' : symbol;
  return `${m[1]} ${fmt.num(Number(m[2]), 0)} · ${Number(m[3])} ${MON[Number(m[4]) - 1]}`;
};

export const REGIME_LABEL: Record<string, string> = {
  'V': 'V (fell ≥ 4%, recovered ≥ 4 pts)',
  'inverted-V': 'Inverted V (rose ≥ 4%, gave back ≥ 4 pts)',
  'trend-down': 'Trend down (≤ −4%)',
  'trend-up': 'Trend up (≥ +4%)',
  'range': 'Range (|end| < 4%)',
  'unclassified': 'Unclassified (no index path)',
};

/** Compact ₹ for chart axes: ₹45K, ₹2.5L, ₹1.2Cr (Indian grouping). */
export const inrShort = (v: number): string => {
  if (!Number.isFinite(v)) return '';
  const a = Math.abs(v), s = v < 0 ? '−' : '';
  if (a >= 1e7) return `${s}₹${fmt.num(a / 1e7, 1)}Cr`;
  if (a >= 1e5) return `${s}₹${fmt.num(a / 1e5, 1)}L`;
  if (a >= 1e3) return `${s}₹${fmt.num(a / 1e3, 0)}K`;
  return `${s}₹${fmt.num(a, 0)}`;
};

/** Axis date: "21 Nov" for spans under ~6 months, "Nov 25" beyond. */
export const axisDate = (t: number, spanSec: number): string => {
  const d = new Date(t * 1000);
  return spanSec > 180 * 86400 ? `${MON[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}` : `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
};

/**
 * The engine writes its reasons in USD ("ES99 loss $515 → $387, … for $26 given up"). Shown
 * beside ₹ figures that reads as two currencies, so each "$<amount>" becomes ₹ at the run's rate.
 */
export const inrText = (text: string, fx: number): string =>
  text
    .replace(/\$(-?\d+(?:\.\d+)?)/g, (_, n) => { const v = Number(n) * fx; return `${v < 0 ? '−' : ''}${fmt.inr(Math.abs(v), 0)}`; })
    .replace(/per \$ /g, 'per ₹ ');

/**
 * Margin use = initial margin / (capital + P&L). Near an exhausted equity it explodes (a real
 * run reached 7,802%): past 999% the honest reading is "the capital is gone", so say that.
 */
export const utilPct = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : v > 999 ? '> 999%' : `${fmt.num(v, 0)}%`;
export const UTIL_HINT = 'Initial margin as a share of equity (capital + P&L). Above 999% the basket had used up its capital; "exhausted" means the equity was at or below zero.';

/** One sentence for a run whose equity went to zero (the simulator models no liquidation). */
export const exhaustedText = (n: number, of: number, first: number | null | undefined): string =>
  `The equity (capital + P&L) was at or below zero in ${n} of ${of} cycles${first != null ? `, from cycle ${first}` : ''}. ` +
  'A real account would have been liquidated there. The simulator models no liquidation and keeps opening baskets, so the results after that point overstate what was possible.';
